import React, { useMemo } from 'react'
import TableColumnPreferencePopover from './TableColumnPreferencePopover'
import { buildColumnConfigFromPreference, isColumnVisible } from './tableColumnPreferences'

/**
 * The column config for the view last saved under `storageKey`: the visible column ids, in order.
 * Ids that are no longer columns are dropped, and nothing saved (or storage the browser won't
 * read) gives the defaults. Use it to seed the config the control is handed.
 */
export const readStoredColumnConfig = (storageKey, defaultColumnConfig = []) => {
    let stored = null
    try {
        stored = JSON.parse(window.localStorage.getItem(storageKey))
    } catch {
        stored = null
    }
    const knownIds = new Set(defaultColumnConfig.map((column) => column.id))
    const visibleIds = Array.isArray(stored) ? stored.filter((columnId) => knownIds.has(columnId)) : []
    return buildColumnConfigFromPreference(defaultColumnConfig, visibleIds)
}

/**
 * The same Customize Columns picker as TableColumnPreferenceControl, but the view is kept in this
 * browser's localStorage instead of on the member.
 */
function LocalStorageColumnPreferenceControl({ storageKey, defaultColumnConfig = [], columnConfig = [], setColumnConfig, buttonId, buttonLabel, placement }) {
    const defaultColumns = useMemo(
        () => buildColumnConfigFromPreference(defaultColumnConfig, []),
        [defaultColumnConfig]
    )

    const saveView = async (draftColumnConfig) => {
        try {
            window.localStorage.setItem(
                storageKey,
                JSON.stringify(draftColumnConfig.filter((column) => isColumnVisible(column)).map((column) => column.id))
            )
        } catch {
            return 'This browser would not save the view.'
        }
        setColumnConfig(draftColumnConfig)
        return true
    }

    return (
        <TableColumnPreferencePopover
            buttonId={buttonId}
            buttonLabel={buttonLabel}
            placement={placement}
            columnConfig={columnConfig}
            defaultColumns={defaultColumns}
            onSave={saveView}
        />
    )
}

export default LocalStorageColumnPreferenceControl
